import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { useFavorites } from "@/hooks/useFavorites";

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { count } = useFavorites();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  const isHome = location.pathname === "/";
  const solid = !isHome || scrolled;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const query = keyword.trim();
    if (!query) return;
    navigate(`/search?query=${encodeURIComponent(query)}`);
    setKeyword("");
    setSearchOpen(false);
  };

  const textColor = solid ? "text-foreground-950" : "text-white";
  const iconHover = solid ? "hover:text-primary-600" : "hover:text-primary-300";

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        solid
          ? "border-b border-background-200 bg-background-50/95 backdrop-blur-md"
          : "border-b border-transparent bg-gradient-to-b from-black/50 to-transparent"
      }`}
    >
      <div className="flex h-16 items-center justify-between gap-4 px-4 md:h-20 md:px-8">
        <Link to="/" className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-lg font-bold text-background-50">
            白
          </span>
          <span className="flex flex-col leading-none">
            <span className={`text-base font-bold tracking-wide ${textColor}`}>国模白露</span>
            <span
              className={`mt-0.5 text-[10px] tracking-[0.3em] ${
                solid ? "text-foreground-500" : "text-white/70"
              }`}
            >
              BAILU GALLERY
            </span>
          </span>
        </Link>

        <nav className={`hidden items-center gap-7 text-sm font-medium md:flex ${textColor}`}>
          <Link to="/" className={`whitespace-nowrap transition-colors ${iconHover}`}>
            首页
          </Link>
          <div className="group relative">
            <button
              type="button"
              className={`flex items-center gap-1 whitespace-nowrap transition-colors ${iconHover}`}
            >
              作品分类
              <i className="ri-arrow-down-s-line" />
            </button>
            <div className="invisible absolute left-1/2 top-full w-56 -translate-x-1/2 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
              <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50 py-2 shadow-sm">
                {categories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/category/${category.id}`}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
                  >
                    <i className="ri-price-tag-3-line text-primary-500" />
                    {category.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <Link to="/category/fashion" className={`whitespace-nowrap transition-colors ${iconHover}`}>
            最新作品
          </Link>
          <Link to="/about" className={`whitespace-nowrap transition-colors ${iconHover}`}>
            关于我们
          </Link>
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <form onSubmit={handleSearch} className="hidden items-center md:flex">
            <div
              className={`flex items-center gap-2 rounded-full border px-3 py-1.5 transition-colors ${
                solid
                  ? "border-background-300 bg-background-100 focus-within:border-primary-400"
                  : "border-white/40 bg-white/10 focus-within:border-primary-300"
              }`}
            >
              <i className={`ri-search-line text-sm ${solid ? "text-foreground-500" : "text-white/80"}`} />
              <input
                type="text"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索作品 / 模特"
                className={`w-28 bg-transparent text-sm outline-none transition-all focus:w-40 lg:w-32 lg:focus:w-48 ${
                  solid ? "text-foreground-900 placeholder:text-foreground-400" : "text-white placeholder:text-white/70"
                }`}
              />
            </div>
          </form>

          <button
            type="button"
            aria-label="搜索"
            onClick={() => setSearchOpen((prev) => !prev)}
            className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors md:hidden ${textColor} ${iconHover}`}
          >
            <i className="ri-search-line text-lg" />
          </button>

          <Link
            to="/favorites"
            aria-label="收藏作品"
            className={`relative flex h-9 w-9 items-center justify-center rounded-full transition-colors ${textColor} ${iconHover}`}
          >
            <i className="ri-heart-3-line text-lg" />
            {count > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] font-semibold text-background-50">
                {count > 99 ? "99+" : count}
              </span>
            )}
          </Link>

          <button
            type="button"
            aria-label="菜单"
            onClick={() => setMenuOpen((prev) => !prev)}
            className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors md:hidden ${textColor} ${iconHover}`}
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-3 md:hidden">
          <form onSubmit={handleSearch} className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-2">
            <i className="ri-search-line text-foreground-500" />
            <input
              ref={searchInputRef}
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索作品 / 模特 / 关键词"
              className="flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            <button type="submit" className="whitespace-nowrap rounded-full bg-primary-500 px-4 py-1.5 text-sm text-background-50">
              搜索
            </button>
          </form>
        </div>
      )}

      {menuOpen && (
        <div className="max-h-[70vh] overflow-y-auto border-t border-background-200 bg-background-50 px-4 py-4 md:hidden">
          <nav className="flex flex-col gap-1">
            <Link to="/" className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100">
              首页
            </Link>
            <Link to="/favorites" className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100">
              我的收藏 {count > 0 && <span className="text-primary-600">({count})</span>}
            </Link>
            <Link to="/about" className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100">
              关于我们
            </Link>
          </nav>
          <div className="mt-3 border-t border-background-200 pt-3">
            <p className="px-3 pb-2 text-xs tracking-widest text-foreground-400">作品分类</p>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/category/${category.id}`}
                  className="flex items-center gap-2 rounded-md bg-background-100 px-3 py-2.5 text-sm text-foreground-700"
                >
                  <i className="ri-price-tag-3-line text-primary-500" />
                  {category.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}